// Overview figures: the pure shapes that the overview pages (the Desk
// Dashboard and the View Firm Overview) draw.
//
// Each function takes the rows of a response and gives the rows of one panel.
// No function here sums two currencies: every money figure on the wire of
// these pages is in GBP already.

import type { KpiReportingTrendPointDto } from '@septima/contract';
import { formatCompactGBP } from '@/lib/format';
// The theme module, not the chart barrel: a feature file reads this module,
// and the barrel would pull the chart library into the entry chunk.
import { withTrueMinus } from '@/components/charts/chart-theme';

/** The dash of an absent figure. An absent figure is never zero. */
export const ABSENT = '—';

/** Money in the compact shape, or the dash when the figure is absent. */
export function money(value: number | null | undefined): string {
    return value == null ? ABSENT : withTrueMinus(formatCompactGBP(value));
}

/** Signed money for a change: `+£1.2M`, `−£250K`, or the dash. */
export function signedMoney(value: number | null | undefined): string {
    if (value == null) return ABSENT;
    const body = formatCompactGBP(Math.abs(value));
    if (value > 0) return `+${body}`;
    if (value < 0) return withTrueMinus(`-${body}`);
    return body;
}

/** A signed percentage to one decimal, or the dash. */
export function signedPct(value: number | null | undefined): string {
    if (value == null) return ABSENT;
    if (value > 0) return `+${value.toFixed(1)}%`;
    return withTrueMinus(`${value.toFixed(1)}%`);
}

/** The RAG tone of a signed figure. An absent figure is neutral. */
export function signTone(value: number | null | undefined): 'positive' | 'negative' | 'neutral' {
    if (value == null || value === 0) return 'neutral';
    return value > 0 ? 'positive' : 'negative';
}

// --- The AUM bridge -----------------------------------------------------------

export interface BridgeRow {
    step: string;
    value: number;
    total: boolean;
}

/**
 * The bridge of a window of monthly KPI points: the opening AUM of the first
 * point, the net monies of the window, the performance, and the closing AUM of
 * the last point.
 *
 * Performance is the closing AUM less the opening AUM less the net monies, so
 * the bars always close on the closing total. Income is part of performance
 * (the KPI rule: income is shown on its own, never deducted), so the bridge
 * does not add it as a fourth step. The caller shows income in the tooltip.
 */
export function bridgeRows(points: readonly KpiReportingTrendPointDto[]): BridgeRow[] {
    const first = points[0];
    const last = points[points.length - 1];
    if (!first || !last) return [];
    const opening = first.aumBFwd;
    const closing = last.aumCFwd;
    const net = points.reduce((sum, p) => sum + p.netMonies, 0);
    return [
        { step: 'Opening', value: opening, total: true },
        { step: 'Net monies', value: net, total: false },
        { step: 'Performance', value: closing - opening - net, total: false },
        { step: 'Closing', value: closing, total: true },
    ];
}

/** The sum of the income of a window of points. */
export function windowIncome(points: readonly KpiReportingTrendPointDto[]): number {
    return points.reduce((sum, p) => sum + p.income, 0);
}

/** `Jan 26–Aug 26`, or one month, for the meta of a panel. */
export function monthSpan(months: readonly string[]): string {
    const first = months[0];
    const last = months[months.length - 1];
    if (!first || !last) return '';
    return first === last ? shortMonth(first) : `${shortMonth(first)}–${shortMonth(last)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-08` to `Aug 26`. Other text passes through. */
export function shortMonth(month: string): string {
    const [y, m] = month.split('-');
    const name = MONTHS[Number(m) - 1];
    return y && name ? `${name} ${y.slice(2)}` : month;
}
